import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getProductsByCategory, getCompanyLogoLocal } from '../api/productApi';
import { getAllCategories } from '../api/categoryApi';
import { useCart } from '../context/CartContext';
import { showCustomToast } from './CustomToast';
import LoadingButton from './LoadingButton';
import './UserCategoryPage.css';
import { resolveImageUrl, resolveOriginalImageUrl } from '../utils/image';
import ProductImageModal from './ProductImageModal';
import ProductVideoModal from './ProductVideoModal';
import { updatePageSEO } from '../utils/seo';

type Product = {
  productId: number;
  productName: string;
  price: number;
  imageUrl: string;
  description: string;
  outOfStock?: boolean;
  videoUrl?: string;
  VideoUrl?: string;
  displayOrder?: number;
  DisplayOrder?: number;
};

export default function UserCategoryPage() {
  const { categoryName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const categoryId = location.state?.categoryId;
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [addingToCart, setAddingToCart] = useState<{ [key: string]: boolean }>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('featured');
  const [imageModal, setImageModal] = useState<{ isOpen: boolean; images: string[]; index: number; productName: string }>({
    isOpen: false,
    images: [],
    index: 0,
    productName: ''
  });
  const [videoModal, setVideoModal] = useState<{ isOpen: boolean; videoUrl: string; productName: string }>({
    isOpen: false,
    videoUrl: '',
    productName: '',
  });
  const { addToCart, cart } = useCart();

  const displayCategory = useMemo(() => {
    if (!categoryName) return 'Firecrackers';
    return categoryName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, [categoryName]);

  useEffect(() => {
    updatePageSEO({
      title: `Buy ${displayCategory} Online Wholesale | Jay Bhavani Fatakda - Jai Bhavani Fireworks`,
      description: `Shop authentic Sivakasi ${displayCategory} at wholesale factory rates from Jay Bhavani Fatakda (Jai Bhavani Fireworks / જય ભવાની ફટાકડા). Direct WhatsApp ordering and offline booking.`,
      canonicalPath: `/${categoryName || ''}`,
      keywords: `${displayCategory.toLowerCase()} fatakda, buy ${displayCategory.toLowerCase()} firecrackers, ${displayCategory.toLowerCase()} wholesale, jay bhavani fatakda, jaibhavani, jaybhavani, jai bhavani fireworks, fatakda online, firecrackers online`
    });
  }, [displayCategory, categoryName]);

  useEffect(() => {
    let isMounted = true;
    const loadProducts = async (catId: number) => {
      try {
        setLoading(true);
        const items = await getProductsByCategory(catId);
        if (!isMounted) return;
        setProducts(items || []);
      } catch (e) {
        if (!isMounted) return;
        showCustomToast('Failed to load products', 'error', 2000);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const resolveAndLoad = async () => {
      if (categoryId) {
        await loadProducts(categoryId);
        return;
      }
      if (categoryName) {
        try {
          const categories = await getAllCategories();
          const list = Array.isArray(categories) ? categories : categories.value || [];
          const normalize = (s: string) => s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          const target = normalize(categoryName);
          const matched = list.find((c: any) => normalize(c.categoryName || c.CategoryName || '') === target);
          if (matched && matched.categoryID) {
            await loadProducts(matched.categoryID);
            return;
          }
          showCustomToast('Category not found', 'error', 2000);
        } catch (e) {
          showCustomToast('Failed to resolve category', 'error', 2000);
        }
      }
    };

    resolveAndLoad();

    return () => { isMounted = false; };
  }, [categoryId, categoryName]);

  const handleAddToCart = async (product: Product) => {
    const productId = product.productId.toString();
    setAddingToCart(prev => ({ ...prev, [productId]: true }));
    try {
      const firstImage = (product as any).imageUrls && (product as any).imageUrls.length > 0
        ? (product as any).imageUrls[0]
        : product.imageUrl || (product as any).productImageUrl || '';

      await addToCart({
        id: productId,
        name: product.productName,
        quantity: 1,
        price: product.price,
        imageUrl: firstImage
      });
      
      const currentCartCount = cart.length + 1;
      showCustomToast(`✓ Added "${product.productName}" to cart! (${currentCartCount} items)`, 'success', 2000);
    } catch (error) {
      showCustomToast('Failed to add to cart', 'error', 2000);
    } finally {
      setAddingToCart(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleImageClick = (images: string[], index: number, productName: string) => {
    setImageModal({
      isOpen: true,
      images: images || [],
      index,
      productName
    });
  };

  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      images: [],
      index: 0,
      productName: ''
    });
  };

  const formattedCategoryTitle = categoryName?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // Filter and sort products
  const processedProducts = useMemo(() => {
    let list = [...products];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p => p.productName?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q));
    }

    if (sortBy === 'price-low') {
      list.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      list.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'name') {
      list.sort((a, b) => a.productName.localeCompare(b.productName));
    } else {
      list.sort((a: any, b: any) => {
        const orderA = (a.displayOrder || a.DisplayOrder) && (a.displayOrder || a.DisplayOrder) > 0 ? (a.displayOrder || a.DisplayOrder) : 999999;
        const orderB = (b.displayOrder || b.DisplayOrder) && (b.displayOrder || b.DisplayOrder) > 0 ? (b.displayOrder || b.DisplayOrder) : 999999;
        if (orderA !== orderB) return orderA - orderB;
        return (a.productId || 0) - (b.productId || 0);
      });
    }

    return list;
  }, [products, searchQuery, sortBy]);

  return (
    <div className="ucp-page">
      {/* Category Header Banner */}
      <div className="ucp-banner">
        <div className="ucp-banner-content">
          <div className="ucp-nav-actions">
            <button className="ucp-back-btn" onClick={() => navigate('/category')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
              <span>All Categories</span>
            </button>
            <div className="ucp-badge">WHOLESALE PRICING</div>
          </div>

          <h1 className="ucp-title">{formattedCategoryTitle} <span>Collection</span></h1>
          <p className="ucp-breadcrumbs">
            Home &nbsp;/&nbsp; Categories &nbsp;/&nbsp; <span>{formattedCategoryTitle}</span>
          </p>
        </div>
      </div>

      {/* Main Container */}
      <div className="ucp-content">
        {/* Controls Bar: Search & Sort */}
        <div className="ucp-controls-bar">
          <div className="ucp-search-wrap">
            <span className="ucp-search-icon">🔍</span>
            <input
              type="text"
              placeholder={`Search in ${formattedCategoryTitle}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="ucp-search-input"
            />
            {searchQuery && (
              <button className="ucp-clear-search" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>

          <div className="ucp-sort-wrap">
            <label htmlFor="product-sort" className="ucp-sort-label">Sort by:</label>
            <select
              id="product-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="ucp-sort-select"
            >
              <option value="featured">Featured Deals</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name">Product Name (A-Z)</option>
            </select>
          </div>
        </div>

        {/* Product Count Strip */}
        <div className="ucp-count-strip">
          <span>Showing <strong>{processedProducts.length}</strong> items in {formattedCategoryTitle}</span>
          <span className="ucp-discount-pill">✨ Up to 50% Off Factory Rates</span>
        </div>

        {/* Products Grid */}
        <div className="ucp-product-grid">
          {loading ? (
            <div className="ucp-loading">
              <div className="ucp-spinner"></div>
              <p>Loading {formattedCategoryTitle} products...</p>
            </div>
          ) : processedProducts.length > 0 ? (
            processedProducts.map(product => {
              const isAdded = cart.some(item => item.id === product.productId.toString());
              const productId = product.productId.toString();
              const mrpPrice = product.price > 0 ? product.price * 2 : 0;

              const rawImages = (product as any).imageUrls && (product as any).imageUrls.length > 0
                ? (product as any).imageUrls
                : product.imageUrl ? [product.imageUrl] : [];
              const allImages = rawImages.map((r: string) => resolveImageUrl(r)).filter(Boolean) as string[];
              const resolved = allImages.length > 0 ? allImages[0] : null;

              const targetVideoUrl = product.videoUrl || (product as any).VideoUrl;

              return (
                <div className="ucp-card" key={product.productId}>
                  <div className="ucp-card-media-wrap">
                    {product.outOfStock ? (
                      <span className="ucp-stock-badge out">OUT OF STOCK</span>
                    ) : (
                      <span className="ucp-stock-badge in">50% OFF</span>
                    )}

                    <div 
                      className="ucp-image-box"
                      onClick={() => handleImageClick(allImages, 0, product.productName)}
                    >
                      {resolved ? (
                        <img
                          src={resolved}
                          alt={product.productName}
                          className="ucp-product-image"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { 
                            const t = e.currentTarget as HTMLImageElement; 
                            const firstRaw = rawImages && rawImages.length > 0 ? rawImages[0] : null;
                            const fallbackUrl = firstRaw ? resolveOriginalImageUrl(firstRaw) : '';
                            if (fallbackUrl && t.src !== fallbackUrl) {
                              t.src = fallbackUrl;
                            } else {
                              t.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="160" height="140" viewBox="0 0 160 140"><rect width="160" height="140" fill="%23f8fafc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="36">🧨</text></svg>';
                            }
                          }}
                        />
                      ) : (
                        <div className="ucp-placeholder">
                          <span>🧨</span>
                        </div>
                      )}
                      <span className="ucp-zoom-hint">🔍 Tap to Zoom</span>
                    </div>
                  </div>

                  <div className="ucp-card-body">
                    {((product as any).companyName || (product as any).CompanyName) && (() => {
                      const comp = (product as any).companyName || (product as any).CompanyName;
                      const compLogo = (product as any).companyLogoUrl || (product as any).CompanyLogoUrl || getCompanyLogoLocal(comp);
                      return (
                        <div className="ucp-card-badges-row">
                          <button
                            type="button"
                            className="ucp-company-tag clickable"
                            onClick={() => {
                              const slug = encodeURIComponent(comp.toLowerCase().trim().replace(/\s+/g, '-'));
                              navigate(`/company/${slug}`, { state: { companyName: comp, companyLogoUrl: compLogo } });
                            }}
                            title={`View all products from ${comp}`}
                          >
                            {compLogo ? (
                              <img
                                src={resolveImageUrl(compLogo)}
                                alt={comp}
                                className="ucp-company-tag-logo"
                                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                              />
                            ) : (
                              <span>🏢</span>
                            )}
                            <span>{comp}</span>
                          </button>
                        </div>
                      );
                    })()}

                    <h3 className="ucp-product-name" title={product.productName}>
                      {product.productName}
                    </h3>
                    
                    {(product.description || (product as any).productDescription) && (
                      <p className="ucp-product-desc">{product.description || (product as any).productDescription}</p>
                    )}

                    <div className="ucp-price-row">
                      {mrpPrice > 0 && (
                        <span className="ucp-mrp">₹{mrpPrice.toFixed(2)}</span>
                      )}
                      <span className="ucp-current-price">₹{product.price.toFixed(2)}</span>
                      <span className="ucp-save-tag">Save ₹{(mrpPrice - product.price).toFixed(0)}</span>
                    </div>

                    {targetVideoUrl && (
                      <button
                        type="button"
                        className="ucp-card-video-action"
                        onClick={() => setVideoModal({
                          isOpen: true,
                          videoUrl: targetVideoUrl,
                          productName: product.productName,
                        })}
                        title="Watch live cracker demonstration video"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                        <span>Watch Demonstration</span>
                      </button>
                    )}

                    <LoadingButton
                      onClick={() => handleAddToCart(product)}
                      loading={addingToCart[productId] || false}
                      disabled={product.outOfStock || isAdded}
                      className={`ucp-add-btn ${isAdded ? 'added' : ''}`}
                    >
                      {product.outOfStock ? 'Out of Stock' : isAdded ? '✓ Added to Cart' : '🛒 Add to Cart'}
                    </LoadingButton>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="ucp-empty">
              <div className="ucp-empty-icon">📦</div>
              <h3>No Products Found</h3>
              <p>{searchQuery ? `No product matched "${searchQuery}".` : "No products currently available in this category. Please check other categories."}</p>
              <button className="ucp-back-cta" onClick={() => navigate('/category')}>
                Browse Other Categories
              </button>
            </div>
          )}
        </div>
      </div>
      
      <ProductImageModal
        isOpen={imageModal.isOpen}
        onClose={closeImageModal}
        images={imageModal.images}
        startIndex={imageModal.index}
        productName={imageModal.productName}
      />
      <ProductVideoModal
        isOpen={videoModal.isOpen}
        onClose={() => setVideoModal({ isOpen: false, videoUrl: '', productName: '' })}
        videoUrl={videoModal.videoUrl}
        productName={videoModal.productName}
      />
    </div>
  );
}